<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { isBoolean } from 'lodash-es';

const props = withDefaults(
  defineProps<{
    label?: string | null;
    labelCols?: number;
    helpTooltip?: string | null;
    helpText?: string | null;
    content?: string | null;
    collapsible?: boolean;
    expandable?: boolean;
    expanderCols?: string | number | null;
    stateKey?: string | null;
    alwaysDisplay?: boolean;
    condensed?: boolean;
  }>(),
  {
    label: null,
    labelCols: 3,
    helpTooltip: null,
    helpText: null,
    content: null,
    collapsible: false,
    expandable: false,
    expanderCols: null,
    stateKey: null,
    alwaysDisplay: false,
    condensed: false,
  },
);

const instance = getCurrentInstance();
const proxy = instance?.proxy as any;
const root = proxy?.$root;
const $app = computed(() => root?.$app);

const isExpandable = computed(() => props.collapsible || props.expandable);

const getIsExpanded = (): boolean => {
  if (isExpandable.value && props.stateKey) {
    const value = $app.value?.objectStore?.getItem(
      `panel.${props.stateKey}`,
      null,
    );
    if (value && isBoolean(value.isExpanded)) {
      return value.isExpanded;
    }
  }
  return false;
};

const isExpanded = ref(getIsExpanded());

watch(isExpanded, (newValue) => {
  if (isExpandable.value && props.stateKey) {
    $app.value?.objectStore?.setItem(`panel.${props.stateKey}`, {
      isExpanded: newValue,
    });
  }
});

const className = computed(() => ({
  'itp-labeled-list-group-item': true,
  'list-group-item': true,
  condensed: props.condensed,
  collapsible: isExpandable.value,
  expanded: isExpanded.value,
}));

const labelClass = computed(() => [
  'itp-labeled-list-group-item__label',
  `col-md-${props.labelCols}`,
]);

const contentClass = computed(() => [
  'itp-labeled-list-group-item__content',
  `col-md-${12 - props.labelCols}`,
]);

const expanderClass = computed(() => {
  const w = props.expanderCols || 9;
  return `col-md-${w} pt-2`;
});

const iconName = computed(() => {
  if (props.collapsible) {
    return isExpanded.value ? 'fa-angle-down' : 'fa-angle-right';
  }
  return undefined;
});

const isVisible = computed(
  () => (props.content !== null && props.content !== '') || props.alwaysDisplay,
);

const onClick = () => {
  if (props.expandable) {
    isExpanded.value = !isExpanded.value;
  }
};
</script>

<template>
  <div :class="className">
    <ItpBox align-items="top">
      <div :class="labelClass">
        <ItpBox
          align-items="center"
          :inline="true"
          :class="{ 'cursor-pointer': isExpandable }"
          @click="onClick"
        >
          <div v-if="iconName" :style="{ width: '1.5rem' }">
            <ItpIcon
              v-if="isExpandable"
              class="text-muted"
              :style="{ width: '1em' }"
              :name="iconName"
            />
          </div>

          <slot name="label">
            <label>{{ label }}</label>
          </slot>

          <sup>
            <i
              v-if="helpTooltip"
              class="fa fa-question-circle text-muted"
              :title="helpTooltip"
            />
          </sup>
        </ItpBox>
      </div>

      <div :class="contentClass">
        <slot>{{ content }}</slot>
      </div>
    </ItpBox>

    <ItpBox v-if="isExpanded" justify-content="end">
      <div :class="expanderClass">
        <slot name="expander" />
      </div>
    </ItpBox>
  </div>
</template>

